import { useState } from "react";
import { Nav } from "../components/Nav";
import { ShortenerForm } from "../components/ShortenerForm";
import { ResultCard } from "../components/ResultCard";
import { Logo } from "../components/Logo";
import { BRAND_TAGLINE } from "../lib/brand";
import { QrCode, LineChart as LineChartIcon, Rocket, ShieldCheck, Layers, Code2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CookiePreferencesButton } from "../components/CookieConsent";

const FEATURES = [
    { icon: QrCode, title: "QR codes, free",
      body: "Every short link ships with a downloadable PNG and SVG QR code. No signup required." },
    { icon: LineChartIcon, title: "Real analytics",
      body: "Track clicks over time with geo, device, browser and referrer breakdowns." },
    { icon: Layers, title: "Multi-tenant ready",
      body: "One API, many workspaces. Perfect for product ecosystems and agencies." },
    { icon: ShieldCheck, title: "Expiry & passwords",
      body: "Protect links with passwords and set automatic expiry dates." },
    { icon: Code2, title: "Drop-in SDK",
      body: "React <ShareButton /> and JS client — wire up any site in minutes." },
    { icon: Rocket, title: "Fast redirects",
      body: "Edge-friendly resolver keeps every hop lightning-quick." },
];

export default function Landing() {
    const [result, setResult] = useState(null);
    const { user } = useAuth();

    return (
        <div className="App min-h-screen">
            <Nav />

            {/* HERO */}
            <section className="relative overflow-hidden border-b border-border">
                <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" aria-hidden />
                <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-[hsl(217,91%,60%)]/10 blur-3xl" aria-hidden />
                <div className="pointer-events-none absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-[hsl(100,76%,42%)]/10 blur-3xl" aria-hidden />

                <div className="relative mx-auto max-w-7xl px-6 pb-20 pt-24 sm:pt-32">
                    <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-8">
                            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium uppercase tracking-widest text-muted-foreground" data-testid="hero-badge">
                                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(100,76%,42%)]" />
                                Powering the SocialPulse ecosystem
                            </div>
                            <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="hero-heading">
                                Shorten, share &<br />
                                <span className="brand-gradient-text">measure everything.</span>
                            </h1>
                            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                                Paste any URL to see your short link and QR code instantly. Register a free account within 30 minutes to <strong className="text-foreground">activate it permanently</strong> and unlock click analytics.
                            </p>
                            <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
                                <Link to="/register" className="font-semibold text-primary hover:underline" data-testid="hero-register-inline">Register for free</Link>{" "}
                                to save permanent links, generate QR codes, and track clicks from your personal dashboard.
                            </p>

                            <div className="mt-10">
                                <ShortenerForm onCreated={setResult} />
                                {result && <ResultCard link={result} />}
                            </div>

                            {!user && !result && (
                                <p className="mt-6 text-sm text-muted-foreground">
                                    Already have an account?{" "}
                                    <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="hero-signin-link">
                                        Sign in
                                    </Link>
                                </p>
                            )}
                        </div>

                        <aside className="hidden lg:col-span-4 lg:block">
                            <div className="sticky top-24 flex flex-col gap-4">
                                <div className="border border-border bg-card p-6">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Ecosystem</div>
                                    <div className="mt-3 font-display text-3xl font-bold">7+ sites</div>
                                    <div className="mt-2 text-sm text-muted-foreground">
                                        socialpulse · foodpulse · kerala · indias · indianfamilytree · myfamilytree · naturpulse
                                    </div>
                                </div>
                                <div className="border border-border bg-card p-6">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Latency</div>
                                    <div className="mt-3 font-display text-3xl font-bold">&lt; 60 ms</div>
                                    <div className="mt-2 text-sm text-muted-foreground">Median redirect time</div>
                                </div>
                                <div className="border border-border bg-card p-6">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Formats</div>
                                    <div className="mt-3 flex gap-2 font-mono text-sm">
                                        <span className="border border-border px-2 py-1">PNG</span>
                                        <span className="border border-border px-2 py-1">SVG</span>
                                        <span className="border border-border px-2 py-1">React SDK</span>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section className="border-b border-border">
                <div className="mx-auto max-w-7xl px-6 py-24">
                    <div className="mb-14 max-w-2xl">
                        <div className="text-xs uppercase tracking-widest text-[hsl(217,91%,45%)]">Features</div>
                        <h2 className="mt-3 font-display text-4xl font-bold tracking-tighter sm:text-5xl">
                            Everything a modern link needs.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 border-l border-t border-border sm:grid-cols-2 lg:grid-cols-3">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="border-b border-r border-border p-8" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
                                <f.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                                <h3 className="mt-5 font-display text-xl font-bold">{f.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SDK CTA */}
            <section className="border-b border-border bg-[hsl(224,15%,7%)] text-[hsl(210,20%,98%)]">
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 py-24 lg:grid-cols-2 lg:items-center">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-[hsl(100,70%,55%)]">Developer SDK</div>
                        <h2 className="mt-3 font-display text-4xl font-bold tracking-tighter sm:text-5xl">
                            Drop-in <span className="brand-gradient-text">React</span> integration.
                        </h2>
                        <p className="mt-5 max-w-lg text-[hsl(215,20%,75%)]">
                            Wire up any SocialPulse child site (or your own product) with a single
                            component. Server-to-server via API key, or client via SDK.
                        </p>
                        <div className="mt-6 flex gap-3">
                            {user ? (
                                <Button asChild data-testid="cta-dashboard-btn">
                                    <Link to="/dashboard">Open dashboard</Link>
                                </Button>
                            ) : (
                                <Button asChild data-testid="cta-signup-btn">
                                    <Link to="/register">Start free</Link>
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="rounded-md border border-[hsl(224,12%,18%)] bg-[hsl(224,12%,10%)] p-6 font-mono text-sm leading-relaxed text-[hsl(210,20%,90%)] shadow-2xl">
                        <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-widest text-[hsl(215,20%,55%)]">
                            <span className="h-2 w-2 rounded-full bg-[hsl(100,76%,42%)]" />
                            example.jsx
                        </div>
                        <pre className="overflow-x-auto whitespace-pre-wrap">{`import { ShareButton } from "@socialpulse/splink";

<ShareButton
  apiKey={process.env.SPLINK_KEY}
  url="https://foodpulse.in/dish/456"
  title="Share this dish"
/>`}</pre>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="mx-auto max-w-7xl px-6 py-12">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <Logo />
                    <div className="flex flex-wrap items-center gap-6 font-mono text-xs text-muted-foreground">
                        <Link to="/about" className="hover:text-foreground" data-testid="footer-about-link">About</Link>
                        <Link to="/terms" className="hover:text-foreground" data-testid="footer-terms-link">Terms</Link>
                        <Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy-link">Privacy</Link>
                        <Link to="/cookies" className="hover:text-foreground" data-testid="footer-cookies-link">Cookies</Link>
                        <Link to="/dpa" className="hover:text-foreground" data-testid="footer-dpa-link">DPA</Link>
                        <CookiePreferencesButton />
                        <span>© {new Date().getFullYear()} Splink.pro · a SocialPulse service</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
